import React, { CSSProperties, useEffect } from 'react'
import { useState } from 'react'
import { Editor } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { EditorState } from 'draft-js'
import { stateFromHTML } from 'draft-js-import-html'
import { stateToHTML } from 'draft-js-export-html'
import { colors } from '@lmc/common/constants'
import './LabelTextBox.less'
import { IconType } from '../../general/Icon/iconTypes'
import Icon from '../../general/Icon'
import InputText from '../InputText'
import { ConsentFormTemplate } from '@lmc/common/types'
import { isMobile } from 'react-device-detect'

export type LabelTextBoxProps = {
  state: 'display' | 'read-only'
  content: any
  localisation: {
    title: string
    subTitle?: string
    readOnly: string
    cancel?: string
    save?: string
    clickToAddTitle?: string
    loseText?: string
  }
  style?: CSSProperties
  onEdit?: () => void
  onCancel?: () => void
  id?: string
  setCopyOfTemplates?: (e: any) => void
  copyOfTemplates?: ConsentFormTemplate[]
  customEntry?: boolean
  onTitleChange?: (e: any) => void
  isMobile?: boolean
}

const getTitle = (title: string, customTitle: string, customEntry: boolean) => {
  if (customEntry) {
    return customTitle === '' ? 'Custom Title' : customTitle
  }
  return title
}

const LabelTextBox = ({
  content,
  localisation,
  state,
  style,
  onEdit,
  onCancel,
  id,
  setCopyOfTemplates,
  copyOfTemplates,
  customEntry,
}: LabelTextBoxProps) => {
  const isReadOnly = state === 'read-only'

  const [editableMode, setEditableMode] = useState(false)

  const [warning, setWarning] = useState(false)

  const componentStyles = {
    container: {
      width: '100%',
      padding: 16,
      borderRadius: 15,
      backgroundColor: `${isReadOnly ? colors.lightgrey : colors.white}`,
    },
    title: { fontWeight: 400, fontSize: 15, display: 'flex' },
    titleContent: {
      opacity: 0.6,
      ...(customEntry && editableMode ? { height: '32px' } : {}),
    },
    subTitle: {
      fontWeight: 300,
      fontSize: 14,
      opacity: 0.6,
    },
    readOnly: {
      display: 'flex',
      marginLeft: 'auto',
      marginRight: 0,
      fontWeight: 700,
      ...(isReadOnly ? { opacity: 0.6 } : { cursor: 'pointer' }),
    },
    pencil: {
      marginLeft: 10,
    },
    trash: { position: 'absolute', right: 20 },
    readOnlyContainer: {
      background: colors.white,
      padding: 18,
      marginTop: 20,
      border: `1px solid ${colors.grey}`,
      borderRadius: 4,
      opacity: 0.7,
    },
    editorStyle: {
      border: `1px solid ${colors.informationblue}`,
      backgroundColor: colors.white,
      padding: 18,
      marginTop: 20,
      opacity: 0.7,
      borderRadius: 4,
    },
    cancelSaveIcon: {
      height: 16,
      marginLeft: 4,
      marginRight: 8,
    },
    transform: {
      justifyContent: isMobile ? 'flex-start' : 'flex-end',
      zIndex: 10,
      marginTop: 10,
    },
    cancelText: {
      color: colors.red,
      fontWeight: 300,
      marginTop: '-4px',
    },
    saveText: {
      color: colors.successgreen,
      fontWeight: 300,
      marginTop: '-4px',
    },
    input: {
      height: '32px',
    },
    loseText: {
      color: colors.red,
      marginTop: '-3px',
      marginRight: 4,
      fontWeight: 700,
      marginLeft: 'auto',
    },
  }

  const [customTitle, setCustomTitle] = useState(
    copyOfTemplates?.find((item) => item.id === id)?.title
  )

  const [hasChangedContent, setHasChangedContent] = useState(false)

  const [contentCopy, setContentCopy] = useState(content)

  const contentState = stateFromHTML(content)

  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(contentState)
  )

  useEffect(() => {
    setContentCopy(content)
    setEditorState(EditorState.createWithContent(contentState))
  }, [id])

  const handleEdit = () => {
    onEdit?.()
    setEditableMode(!editableMode)
  }

  const handleCancel = () => {
    if (hasChangedContent) {
      setWarning(true)
    }
    if (!hasChangedContent || warning) {
      onCancel?.()
      setEditableMode(!editableMode)
      const newContentState = stateFromHTML(contentCopy)
      setEditorState(EditorState.createWithContent(newContentState))
      setHasChangedContent(false)
      setCustomTitle(copyOfTemplates?.find((item) => item.id === id)?.title)
      setWarning(false)
    }
  }

  const handleSave = () => {
    setEditableMode(!editableMode)
    setWarning(false)
    const newState = stateToHTML(editorState.getCurrentContent())
    setContentCopy(newState)
    setHasChangedContent(false)
    const newTemplates = copyOfTemplates?.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          body: newState,
          // @ts-ignore
          title: getTitle(item.title, customTitle, customEntry),
        }
      }
      return item
    })
    setCopyOfTemplates?.(newTemplates)
  }

  const handleUpdateTitle = (e: any) => {
    setCustomTitle?.(e.target.value)
    if (e.target.value.length !== customTitle?.length) {
      setHasChangedContent(true)
    }
  }

  const handleRemove = () => {
    setWarning(true)
    if (warning) {
      const filteredCustomForms = copyOfTemplates?.filter(
        (item) => item.id !== id
      )
      setCopyOfTemplates?.(filteredCustomForms)
      setWarning(false)
    }
  }

  const onEditorStateChange = (editorState: {
    getCurrentContent: () => any
  }) => {
    // @ts-ignore
    setEditorState(editorState)
    const newState = stateToHTML(editorState.getCurrentContent())
    if (newState.length !== contentCopy.length) {
      setHasChangedContent(true)
    }
  }

  return (
    <div id="label-text-box" style={{ ...componentStyles.container, ...style }}>
      <div style={componentStyles.title}>
        <div style={componentStyles.titleContent}>
          {!customEntry || !editableMode ? (
            localisation.title
          ) : (
            <InputText
              id="title-input-editor"
              // eslint-disable-next-line no-return-assign
              value={customTitle}
              onChange={handleUpdateTitle}
              placeholder={
                localisation?.clickToAddTitle || 'Click to add a Title'
              }
            />
          )}
        </div>
        {!isReadOnly && (
          <>
            {!customEntry && warning && (
              <div style={componentStyles.loseText as CSSProperties}>
                {localisation.loseText}
              </div>
            )}
            {customEntry && (
              <div
                style={
                  {
                    ...componentStyles.readOnly,
                    ...(editableMode && componentStyles.trash),
                  } as CSSProperties
                }
                onClick={() => handleRemove()}
              >
                {warning && (
                  <div style={componentStyles.loseText as CSSProperties}>
                    {localisation.loseText}
                  </div>
                )}
                <Icon name="DeleteFilled" />
              </div>
            )}
            {!editableMode && (
              <div
                id="edit-button"
                style={{
                  ...componentStyles.readOnly,
                  ...(customEntry && componentStyles.pencil),
                }}
                onClick={() => handleEdit()}
              >
                <Icon customIcon={IconType.Pencil} />
              </div>
            )}
          </>
        )}
        {isReadOnly && (
          <div style={componentStyles.readOnly}>{localisation.readOnly}</div>
        )}
      </div>
      {localisation.subTitle && (
        <div style={componentStyles.subTitle}>{localisation.subTitle}</div>
      )}
      {!isReadOnly && editableMode && (
        <div
          style={
            {
              ...componentStyles.readOnly,
              ...componentStyles.transform,
            } as CSSProperties
          }
        >
          {hasChangedContent && (
            <>
              <div
                style={componentStyles.saveText}
                onClick={() => handleSave()}
                id="save-button"
              >
                {localisation.save}
              </div>
              <Icon
                onClick={() => handleSave()}
                style={componentStyles.cancelSaveIcon}
                customIcon={IconType.Save}
              />
            </>
          )}
          <div
            id="cancel-button"
            style={componentStyles.cancelText}
            onClick={() => handleCancel()}
          >
            {localisation.cancel}
          </div>
          <Icon
            onClick={() => handleCancel()}
            style={componentStyles.cancelSaveIcon}
            customIcon={IconType.Cancel}
          />
        </div>
      )}
      {!isReadOnly &&
        (editableMode ? (
          <div style={componentStyles.editorStyle}>
            <Editor
              // @ts-ignore
              id="text-editor"
              editorState={editorState}
              toolbarClassName="toolbarClassName"
              wrapperClassName="wrapperClassName"
              editorClassName="editorClassName"
              onEditorStateChange={onEditorStateChange}
              toolbar={{
                options: ['fontSize', 'inline', 'history'],
                fontSize: {
                  options: [16, 18, 22, 26, 30],
                },
                inline: {
                  options: ['bold', 'italic', 'underline', 'strikethrough'],
                },
              }}
            />
          </div>
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: contentCopy }}
            style={componentStyles.readOnlyContainer}
          />
        ))}
      {isReadOnly && (
        <div
          dangerouslySetInnerHTML={{ __html: content }}
          style={componentStyles.readOnlyContainer}
        />
      )}
    </div>
  )
}

export default LabelTextBox
